import { Link } from "react-router-dom";
import { ShieldCheck, ArrowLeft } from "lucide-react";
import { LegalRequestForm } from "@/components/LegalRequestForm";

const S = ({ title, children }) => (
  <section className="mt-10">
    <h2 className="font-display text-xl sm:text-2xl font-semibold tracking-tight">{title}</h2>
    <div className="mt-3 space-y-3 text-sm sm:text-base text-gray-600 leading-relaxed">{children}</div>
  </section>
);

export default function PrivacyPage() {
  return (
    <div className="min-h-screen bg-paper">
      <header className="backdrop-blur-xl bg-white/70 border-b border-white/40">
        <div className="max-w-3xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" data-testid="privacy-home-link" className="flex items-center gap-2.5">
            <span className="w-8 h-8 rounded-lg bg-trust flex items-center justify-center">
              <ShieldCheck size={16} className="text-white" />
            </span>
            <span className="font-display font-bold text-lg tracking-tight">ProofBuy</span>
          </Link>
          <Link to="/" className="inline-flex items-center gap-1.5 text-sm font-semibold text-gray-500 hover:text-trust transition-colors duration-200">
            <ArrowLeft size={15} /> Back
          </Link>
        </div>
      </header>

      <main data-testid="privacy-page" className="max-w-3xl mx-auto px-6 py-14">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-trust">Legal</p>
        <h1 className="mt-3 font-display text-4xl sm:text-5xl font-bold tracking-tight">Privacy Policy</h1>
        <p className="mt-3 text-sm text-gray-500">Last updated: 24 August 2026</p>
        <p className="mt-5 text-base text-gray-600 leading-relaxed">
          This policy explains what ProofBuy collects, why, and what you can do about it — in plain English.
          If anything here is unclear, write to us:{" "}
          <a href="mailto:support@proofbuy.in" className="text-trust font-semibold">support@proofbuy.in</a>.
        </p>

        <S title="1. Who we are">
          <p>
            ProofBuy (proofbuy.in) is an India-based service where buyers of digital products and online courses
            share verified, first-hand purchase experiences — backed by proof, not opinions. For anything about your
            data, contact us at <a href="mailto:support@proofbuy.in" className="text-trust font-semibold">support@proofbuy.in</a>.
          </p>
        </S>

        <S title="2. What we collect">
          <ul className="list-disc pl-5 space-y-2">
            <li><b>Email address</b> — when you join the early-access waitlist or create a reviewer account.</li>
            <li><b>Account details</b> — your name or display name, and your password (stored only as a bcrypt hash — we can never see or read your actual password).</li>
            <li><b>Review content</b> — what you paid, what was promised, what you received, and your answers about upsells, refunds, and resell-rights.</li>
            <li><b>Uploaded proofs</b> — your payment screenshot and product screenshot.</li>
            <li><b>Basic technical data</b> — your IP address and device/browser information, used only for rate-limiting and abuse prevention.</li>
          </ul>
        </S>

        <S title="3. Why we collect it">
          <ul className="list-disc pl-5 space-y-2">
            <li>To verify that reviews come from real buyers with real purchases.</li>
            <li>To run the service — accounts, submissions, verification, search.</li>
            <li>To prevent fraud, spam, and fake reviews (ours is a proof-based platform; that only works with these checks).</li>
            <li>To send service emails — welcome notes, publish notifications, password resets, and waitlist announcements (every waitlist email has an unsubscribe link).</li>
          </ul>
        </S>

        <S title="4. Your payment screenshots — the important one">
          <ul className="list-disc pl-5 space-y-2">
            <li>Stored <b>privately</b>. Visible <b>only</b> to ProofBuy admins, and only for verification.</li>
            <li><b>Never published publicly.</b> Search results and public pages never show your proofs.</li>
            <li>Location/EXIF metadata is <b>stripped automatically</b> when you upload an image.</li>
            <li><b>Before uploading, redact your bank/UPI account number.</b> Only the amount, date, and payee need to be visible. If you forget, our admins see the screenshot only to verify it — but redacting is always the safer habit.</li>
          </ul>
        </S>

        <S title="5. Your rights under India's DPDP Act, 2023">
          <p>Under the Digital Personal Data Protection Act, 2023, you have the right to:</p>
          <ul className="list-disc pl-5 space-y-2">
            <li><b>Access</b> — ask what data we hold about you.</li>
            <li><b>Correct</b> — ask us to fix wrong or incomplete data.</li>
            <li><b>Delete</b> — ask us to delete your data, including your account, reviews, and uploaded proofs.</li>
          </ul>
          <p>
            To use any of these rights, email <a href="mailto:support@proofbuy.in" className="text-trust font-semibold">support@proofbuy.in</a> from
            the email address on your account. We honor deletion requests — once verified, we remove your data from
            our active systems and tell you when it's done.
          </p>
        </S>

        <S title="6. Who we share data with">
          <p><b>We do not sell your data.</b> Ever. We use a small number of third-party processors to run the service:</p>
          <ul className="list-disc pl-5 space-y-2">
            <li><b>Hosting and database provider</b> — stores the site's data (currently Emergent's cloud hosting with MongoDB).</li>
            <li><b>Email service</b> — sends transactional and waitlist emails (currently Resend, via Emergent's managed integration).</li>
            <li><b>File storage</b> — stores uploaded proof screenshots privately (Emergent object storage).</li>
          </ul>
          <p>These processors only handle your data to provide their service to us — nothing more.</p>
        </S>

        <S title="7. How long we keep data">
          <p>
            We keep your account, reviews, and proofs for as long as your account exists or the review is live —
            that's the point of a public record. Rate-limiting and abuse-prevention records are kept for short
            rolling windows (hours to days). You can request removal of your data at any time (see Section 5).
          </p>
        </S>

        <S title="8. Security">
          <p>
            Passwords are stored only as bcrypt hashes. Proof images are access-restricted to admins and served
            through authenticated storage — not public links. Login and submission endpoints are rate-limited to
            resist brute force and spam.
          </p>
        </S>

        <S title="9. Children">
          <p>ProofBuy is for adults only (18+). We do not knowingly collect data from anyone under 18.</p>
        </S>

        <S title="10. Changes to this policy">
          <p>
            If we change this policy, we'll update the date at the top and, for significant changes, email the
            address on your account or show a notice on the site before the change takes effect.
          </p>
        </S>

        <S title="11. Contact">
          <p>
            Questions, access/correction/deletion requests, or anything about your data:{" "}
            <a href="mailto:support@proofbuy.in" className="text-trust font-semibold">support@proofbuy.in</a>
          </p>
        </S>

        <LegalRequestForm />
      </main>
    </div>
  );
}
